/**
 * The guest's cart.
 *
 * Deliberately separate from the POS cart: the customer menu's shapes are
 * different (already-localised names, resolved prices, no `nameVi`), and mixing
 * them would put staff-only fields on a guest's screen.
 *
 * The cart holds `priceMinor` from the menu only to show a running estimate.
 * The API accepts NO price on `POST /customer/orders` — it re-prices everything
 * server-side — which is exactly what stops a tampered phone from ordering a
 * lobster for 1 ₫.
 */

import type { CustomerMenuModifier, CustomerMenuProduct, CustomerMenuVariant, CustomerOrderItemInput } from '../lib/types';

export interface GuestCartModifier {
  readonly modifierId: string;
  readonly name: string;
  readonly priceDeltaMinor: number;
  readonly quantity: number;
}

export interface GuestCartLine {
  readonly key: string;
  readonly productId: string;
  readonly productName: string;
  readonly variantId: string | null;
  readonly variantName: string | null;
  readonly unitPriceMinor: number;
  readonly quantity: number;
  readonly note: string | null;
  readonly spiceLevel: number | null;
  readonly modifiers: readonly GuestCartModifier[];
}

export type GuestCart = readonly GuestCartLine[];

let seq = 0;

export function guestLineTotal(line: GuestCartLine): number {
  const mods = line.modifiers.reduce((acc, m) => acc + m.priceDeltaMinor * m.quantity, 0);
  return (line.unitPriceMinor + mods) * line.quantity;
}

export function guestCartTotal(cart: GuestCart): number {
  return cart.reduce((acc, line) => acc + guestLineTotal(line), 0);
}

export function guestCartCount(cart: GuestCart): number {
  return cart.reduce((acc, line) => acc + line.quantity, 0);
}

export function addGuestLine(
  cart: GuestCart,
  product: CustomerMenuProduct,
  variant: CustomerMenuVariant | null,
  modifiers: readonly CustomerMenuModifier[],
  quantity: number,
  note: string | null,
  spiceLevel: number | null,
): GuestCart {
  seq += 1;
  const line: GuestCartLine = {
    key: `g-${Date.now().toString(36)}-${seq}`,
    productId: product.id,
    productName: product.name,
    variantId: variant?.id ?? null,
    variantName: variant?.name ?? null,
    // The customer menu already resolves branch/day-part pricing, so the
    // variant carries an absolute `priceMinor` as well as a delta.
    unitPriceMinor: variant === null ? product.priceMinor : variant.priceMinor,
    quantity: Math.max(1, Math.round(quantity)),
    note: note === null || note.trim() === '' ? null : note.trim(),
    spiceLevel,
    modifiers: modifiers.map((m) => ({
      modifierId: m.id,
      name: m.name,
      priceDeltaMinor: m.priceDeltaMinor,
      quantity: 1,
    })),
  };
  return [...cart, line];
}

export function setGuestQuantity(cart: GuestCart, key: string, quantity: number): GuestCart {
  if (quantity <= 0) return cart.filter((line) => line.key !== key);
  return cart.map((line) => (line.key === key ? { ...line, quantity: Math.round(quantity) } : line));
}

export function removeGuestLine(cart: GuestCart, key: string): GuestCart {
  return cart.filter((line) => line.key !== key);
}

/** Cart → `POST /customer/orders` body. No prices, no names, ids only. */
export function toGuestOrderItems(cart: GuestCart): CustomerOrderItemInput[] {
  return cart.map((line) => ({
    productId: line.productId,
    variantId: line.variantId,
    quantity: line.quantity,
    modifiers: line.modifiers.map((m) => ({ modifierId: m.modifierId, quantity: m.quantity })),
    note: line.note,
    spiceLevel: line.spiceLevel,
  }));
}
